/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { delay, http, HttpResponse } from 'msw';
import workspacePresets from './workspacePresets.json';
import type {
  WorkspacePresetFromBE,
  WorkspacePresetListItem,
} from '../../../store/workspace/types';
import { LABELS_CONFIG_DEFAULT } from '../../../utils/labels.config';

const defaultWorkspacePresets = workspacePresets as WorkspacePresetFromBE[];

// Transforms full workspace presets to list items
const workspacePresetList: WorkspacePresetListItem[] =
  defaultWorkspacePresets.map((preset) => ({
    id: preset.id,
    title: preset.title,
    scope: preset.scope,
    viewType: preset.viewType,
    abstract: preset.abstract ?? '',
    description: preset.description,
    username: preset.username,
  }));

export const workspaceEndpoints = [
  http.delete(/^.*\/workspacepreset\/(.*)$/, async () => {
    await delay();
    return HttpResponse.json(null);
  }),
  http.post(/^.*\/workspacepreset$/, async () => {
    await delay();
    return HttpResponse.json(null, {
      status: 200,
      headers: {
        Location: '/workspacepreset/workspace-id-1',
      },
    });
  }),
  http.put(/^.*\/workspacepreset\/(.*)$/, async () => {
    await delay();
    return HttpResponse.json(null);
  }),
  http.get(/^.*\/workspacepreset\/(.*)$/, async ({ params }) => {
    await delay();
    const presetId = String(params[0] ?? '');
    const preset = defaultWorkspacePresets.find((item) => item.id === presetId);
    if (!preset) {
      return HttpResponse.json({ message: 'Not found' }, { status: 404 });
    }
    return HttpResponse.json(preset);
  }),
  http.get(/.*\/workspacepreset\/?$/, async () => {
    await delay();
    return HttpResponse.json(workspacePresetList);
  }),

  http.get(/^.*\/workspace-labels-config$/, async () => {
    return HttpResponse.json(LABELS_CONFIG_DEFAULT);
  }),
  http.get(/.*plugins\/metadata.*/, async () => {
    return HttpResponse.json({});
  }),
];
